import { useState, useEffect } from 'react'
import DashboardBody from '../components/DashboardBody'
import { useNavigate } from 'react-router-dom'
import { Modal, Select, Alert, Spin } from 'antd'
import axiosInstance from '../components/lib/axiosInstance'
import { LoadingOutlined } from '@ant-design/icons'
import { HttpResponse, Project } from '../components/commons'

const { Option } = Select

const Dashboard = () => {
  const [isModalVisible, setIsModalVisible] = useState(true)
  const [projects, setProjects] = useState<Project[]>([])
  const [selectedProject, setSelectedProject] = useState<string>('')
  const [companyId, setCompanyId] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string>('')

  const navigate = useNavigate()

  useEffect(() => {
    // Retrieve companyId and selectedProject from localStorage
    const auth = localStorage.getItem('auth')
    const savedProjectId = localStorage.getItem('selectedProject')

    if (auth) {
      const { companyId } = JSON.parse(auth)
      setCompanyId(companyId)
    }

    if (savedProjectId) {
      setSelectedProject(savedProjectId)
      setIsModalVisible(false) // Don't show modal if a project is already selected
    }
  }, [])

  useEffect(() => {
    if (companyId) {
      fetchProjects()
    }
  }, [companyId])

  const fetchProjects = async () => {
    setIsLoading(true)
    setError('')
    try {
      const response = await axiosInstance.get<any, HttpResponse<Project[]>>('/api/v1/Projects/GetByCompany', {
        params: { CompanyId: companyId },
      })
      setProjects(response.data.data)
    } catch (error) {
      console.error('Error fetching projects:', error)
      setError('Failed to fetch projects. Please try again later.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleProjectSelect = (value: string) => {
    setSelectedProject(value)
    localStorage.setItem('selectedProject', value) // Save selected project to localStorage
  }

  const handleProceed = () => {
    if (selectedProject) {
      setIsModalVisible(false)
    }
  }

  const handleRetry = () => {
    fetchProjects()
  }

  const handleCancel = () => {
    navigate('/login')
  }


  // Determine button styles based on the selected project state
  const okButtonStyle = selectedProject
    ? {
        backgroundColor: 'black',
        color: 'white',
        borderColor: 'black',
        cursor: 'pointer',
      }
    : {
        backgroundColor: 'gray',
        color: 'white',
        borderColor: 'gray',
        cursor: 'not-allowed',
      }

  return (
    <div className="min-h-screen bg-white">
      
      {selectedProject && (
        <DashboardBody selectedProjectId={selectedProject} companyId={companyId} />
      )}

      {!selectedProject && (
        <Modal
          title={
            <div className="text-lg font-semibold">
              Select a Project
              <div className="text-sm font-normal text-gray-500 mt-1">
                Select a project to proceed
              </div>
            </div>
          }
          visible={isModalVisible}
          onOk={handleProceed}
          onCancel={handleCancel}
          okButtonProps={{
            disabled: !selectedProject,
            style: okButtonStyle,
            className: selectedProject ? 'button-hover' : '',
          }}
          closable={false}
          maskClosable={false}
          okText="Proceed"
          width={400}
        >
          {isLoading ? (
            <div className="text-center py-4">
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              />
              <p className="mt-2">Loading projects...</p>
            </div>
          ) : error ? (
            <Alert
              message="Error"
              description={error}
              type="error"
              showIcon
              action={
                <button
                  onClick={handleRetry}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  Retry
                </button>
              }
            />
          ) : projects.length === 0 ? (
            <Alert
              message="No Projects Available"
              description="You don't have any projects yet. Please create a project to get started."
              type="info"
              showIcon
            />
          ) : (
            <Select
              style={{ width: '100%' }}
              placeholder="Select a project"
              onChange={handleProjectSelect}
              value={selectedProject} // Ensure the selected project is shown in the Select component
            >
              {projects?.map((project) => (
                <Option key={project.id} value={project.id}>
                  {project.name}
                </Option>
              ))}
            </Select>
          )}
        </Modal>
      )}
    </div>
  )
}

export default Dashboard
